import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Crown, Sparkles, Tag, Loader2, TrendingUp, Trophy } from "lucide-react";
import api from "../lib/api";

const TIER_META = {
  "hyper-premium": { label: "Hyper-premium", icon: Crown, accent: "text-amber-300", glow: "rgba(245,158,11,0.18)" },
  premium: { label: "Premium", icon: Sparkles, accent: "text-fuchsia-300", glow: "rgba(232,121,249,0.18)" },
  mainline: { label: "Mainline", icon: Tag, accent: "text-zinc-300", glow: "rgba(161,161,170,0.10)" },
  unknown: { label: "Other", icon: Tag, accent: "text-zinc-400", glow: "rgba(161,161,170,0.05)" },
};

export default function BrandDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api.get(`/brands/${encodeURIComponent(slug)}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e?.response?.status;
        setError(status === 404 ? "Brand not found." : "Couldn't load this brand.");
      });
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center text-center p-8" data-testid="brand-detail-error">
        <p className="text-rose-400 mb-4">{error}</p>
        <Link to="/brands" className="text-fuchsia-300 hover:text-fuchsia-200 text-sm" data-testid="brand-detail-back">← Back to Brand Index</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="brand-detail-loading">
        <Loader2 className="w-10 h-10 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const meta = TIER_META[data.tier] || TIER_META.unknown;
  const Icon = meta.icon;
  const msrp = data.msrp_low && data.msrp_high ? `$${data.msrp_low.toFixed(0)}–$${data.msrp_high.toFixed(0)}` : "MSRP unknown";
  const stats = data.stats || {};

  return (
    <div className="min-h-screen text-white" data-testid="brand-detail-page" style={{
      background: `radial-gradient(circle at 80% -10%, ${meta.glow} 0%, transparent 55%), linear-gradient(160deg,#06060a 0%,#0a0814 60%,#15081e 100%)`,
    }}>
      <header className="px-6 sm:px-10 py-5 border-b border-white/5">
        <Link to="/brands" className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-white" data-testid="brand-detail-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> All brands
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-12">
        {/* Hero */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className={`text-xs uppercase tracking-[0.4em] font-semibold flex items-center gap-2 ${meta.accent}`}>
              <Icon className="w-3.5 h-3.5" /> {meta.label}
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl font-black mt-3 tracking-tight leading-[1.05]"
                style={{ background: "linear-gradient(135deg,#fff 0%,#fbcfe8 60%,#f0abfc 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
                data-testid="brand-detail-name">
              {data.canonical}
            </h1>
            <p className="text-zinc-400 mt-4 max-w-2xl text-sm sm:text-base leading-relaxed">{data.notes}</p>
          </div>
          <div className="lg:text-right">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 ${meta.accent}`}>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Typical MSRP</span>
              <span className="font-mono font-bold text-base">{msrp}</span>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Releases tracked" value={(stats.release_count ?? 0).toLocaleString()} icon={Tag} accent="text-white" />
          <Stat label="Owners" value={(stats.owner_count ?? 0).toLocaleString()} icon={Trophy} accent="text-fuchsia-300" />
          <Stat label="Chases" value={(stats.chase_count ?? 0).toLocaleString()} icon={Crown} accent="text-amber-300" />
          <Stat
            label="Avg market value"
            value={stats.avg_market_value != null ? `$${Math.round(stats.avg_market_value)}` : "—"}
            icon={TrendingUp}
            accent="text-rose-300"
          />
        </div>

        {/* Empty-brand awaiting state */}
        {(stats.release_count ?? 0) === 0 && (
          <div
            data-testid="brand-detail-awaiting"
            className="mt-10 rounded-2xl bg-gradient-to-br from-zinc-900/70 to-zinc-900/30 ring-1 ring-white/5 px-6 py-8 text-center"
          >
            <Sparkles className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="text-zinc-200 font-semibold mt-3">No tracked releases yet for {data.canonical}</p>
            <p className="text-zinc-500 text-sm mt-1 max-w-md mx-auto">
              We've registered this brand but haven't logged a release yet. Be the first — suggest a release from the catalog and an admin will add it.
            </p>
            <Link
              to="/login"
              data-testid="brand-detail-suggest"
              className="inline-flex items-center mt-5 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-semibold"
            >
              Sign in to suggest →
            </Link>
          </div>
        )}

        {/* Top Releases */}
        {data.top_releases?.length > 0 && (
          <section className="mt-14" data-testid="brand-detail-top-releases">
            <h2 className="font-heading text-2xl font-extrabold text-white tracking-tight">Top releases by value</h2>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.top_releases.map((r) => (
                <li key={r.release_id} className="rounded-2xl bg-zinc-900/60 ring-1 ring-white/5 hover:ring-fuchsia-500/20 p-4 flex gap-3 transition">
                  {r.model_image ? (
                    <img src={r.model_image} alt="" className="w-20 h-20 rounded-lg object-cover bg-zinc-800 ring-1 ring-white/5" />
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-zinc-800 ring-1 ring-white/5 flex items-center justify-center text-zinc-600 text-xs">no image</div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-white text-sm leading-tight line-clamp-2">{r.release_name}</p>
                    <p className="text-zinc-500 text-[11px] mt-1">{r.vehicle}</p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {r.is_chase && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Chase</span>}
                      {r.is_blkltd && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30">BLKLTD</span>}
                      {r.estimated_market_value && (
                        <span className="font-mono text-amber-300 font-bold text-sm ml-auto">${Math.round(r.estimated_market_value)}</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Top Collectors */}
        {data.top_collectors?.length > 0 && (
          <section className="mt-14" data-testid="brand-detail-top-collectors">
            <h2 className="font-heading text-2xl font-extrabold text-white tracking-tight">Top collectors</h2>
            <ul className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {data.top_collectors.map((c) => (
                <li key={c.user_id}>
                  <Link
                    to={`/collector/${c.profile_slug || c.user_id}`}
                    className="block rounded-xl bg-zinc-900/60 ring-1 ring-white/5 hover:ring-fuchsia-500/30 p-3 text-center transition"
                  >
                    {c.profile_photo ? (
                      <img src={c.profile_photo} alt="" className="w-14 h-14 rounded-full mx-auto object-cover ring-2 ring-white/10" />
                    ) : (
                      <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 ring-2 ring-white/10 flex items-center justify-center font-bold text-lg text-zinc-400">
                        {(c.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <p className="text-xs font-semibold text-white mt-2 truncate">{c.name || c.profile_slug}</p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{c.pieces} pieces</p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* CTA */}
        <div className="mt-16 rounded-2xl bg-gradient-to-r from-fuchsia-500/10 to-rose-500/10 border border-fuchsia-500/20 px-6 py-7 text-center">
          <p className="text-amber-300 text-[10px] uppercase tracking-[0.4em] font-semibold">Castfolio</p>
          <h3 className="text-white font-heading text-2xl font-bold mt-2">Track your {data.canonical} pieces</h3>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            Build a collection portfolio with AI-powered market estimates anchored to {data.canonical}'s real MSRP and tier.
          </p>
          <Link
            to="/register"
            data-testid="brand-detail-cta"
            className="inline-flex items-center mt-5 px-5 py-2.5 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 hover:from-rose-400 hover:to-fuchsia-400 text-white text-sm font-semibold"
          >
            Start your portfolio →
          </Link>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl bg-zinc-900/60 ring-1 ring-white/5 p-4">
      <Icon className="w-4 h-4 text-zinc-600 mb-2" />
      <div className={`font-mono text-2xl font-bold ${accent}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
